import { Injectable } from '@angular/core';
import { all, object } from 'underscore';
import { KeyPair } from '../models/base';
import { CompanyDeductionConfig } from '../models/company-deduction-config';
import { CompanyEarningConfig, EarningCalculations } from '../models/company-earning-config';
import { LaborAllocationTypes } from '../models/labor-allocation';
import {
  AllocatedEarning,
  Deduction,
  Earning,
  EmployeePay,
  LaborAllocation,
  Payline,
  PaylineType,
  PayrollConfigSetupData,
  Tax
} from '../models/payroll';

@Injectable({
  providedIn: 'root'
})
export class LaborAllocationService {
  private allocTypeId: number;
  private configSetupData: PayrollConfigSetupData;
  private jobCodes: KeyPair[];

  constructor() {}

  public init(allocTypeId: number, configSetupData: PayrollConfigSetupData, jobCodes: KeyPair[]) {
    this.allocTypeId = allocTypeId;
    this.configSetupData = configSetupData;
    this.jobCodes = jobCodes;
  }

  public getDeductionName(deductionId: string) {
    return this._getDeductionConfig(deductionId)?.Name;
  }

  public getEarningName(earningId: string) {
    return this._getEarningConfig(earningId)?.Name;
  }

  public CalculateTotalHours(earn: Earning, payline: Payline) {
    const config = this._getEarningConfig(earn.EarningId);

    if (!config.CanSetHours) {
      return null;
    }

    const allocs = Object.values(payline.Allocations);
    if (allocs.some(alloc => alloc.TemporaryHourlyAllocations[earn.EarningId]?.Hours != null)) {
      let sum = 0;
      allocs.forEach(alloc => {
        if (alloc.TemporaryHourlyAllocations[earn.EarningId]?.Hours != null) {
          sum += +alloc.TemporaryHourlyAllocations[earn.EarningId].Hours;
        }
      });
      return sum;
    } else {
      return +earn.Hours;
    }
  }

  public CalculateHours(earn: Earning, allocation: LaborAllocation) {
    const config = this._getEarningConfig(earn.EarningId);

    if (!config.CanSetHours) {
      return null;
    }

    if (this.IsPercentageAllocation(allocation)) {
      return +earn.Hours * this.GetPercentageAllocation(allocation);
    }

    return allocation.TemporaryHourlyAllocations[earn.EarningId]?.Hours;
  }

  public CalculateTotalRate(earn: Earning, payline: Payline) {
    var allAllocs = Object.values(payline.Allocations);
    if ((allAllocs?.length ?? 0) == 0) {
      return null;
    }

    // Get the first allocation with a temporary rate
    var alloc = allAllocs.find(alloc => alloc.TemporaryHourlyAllocations?.[earn.EarningId] != null);

    if (alloc == null) {
      return earn.TemporaryRateAmount || earn.PermanentRateAmount;
    }

    const hours = this.CalculateTotalHours(earn, payline) || +earn.Hours;

    return hours ? earn.CurrentAmount / hours : earn.CurrentAmount;
  }

  public CalculateRate(earn: Earning, payline: Payline, allocation: LaborAllocation) {
    const config = this._getEarningConfig(earn.EarningId);

    if (this.IsPercentageAllocation(allocation)) {
      if (
        config.CalculationMethod == EarningCalculations['Use Rate From'] &&
        config.SourceEarning != null
      ) {
        var sourceEarn = payline.Earnings.find(e => e.EarningId == config.SourceEarning);
        if (sourceEarn != null) {
          return (
            config.UseRateFactor *
            (sourceEarn.TemporaryRateAmount || sourceEarn.PermanentRateAmount)
          );
        }
      }

      if (earn.Hours || config.CalculationMethod == EarningCalculations['Percentage of Earning']) {
        return earn.TemporaryRateAmount || earn.PermanentRateAmount;
      }

      return (
        this.GetPercentageAllocation(allocation) *
        (earn.TemporaryRateAmount || earn.PermanentRateAmount)
      );
    }

    const a = allocation.TemporaryHourlyAllocations[earn.EarningId];

    if (!a) {
      return null;
    }

    return a.Hours
      ? a.ResultAmount / a.Hours
      : (a.TemporaryRateAmount ?? a.PermanentRateAmount ?? a.ResultAmount);
  }

  public IsHourlyAllocation(allocation: LaborAllocation) {
    return (
      allocation.TemporaryHourlyAllocations != null &&
      Object.keys(allocation.TemporaryHourlyAllocations).length != 0
    );
  }

  public IsPercentageAllocation(allocation: LaborAllocation) {
    return !this.IsHourlyAllocation(allocation);
  }

  // Returns the temporary or permanent allocation value as a decimal
  public GetPercentageAllocation(allocation: LaborAllocation) {
    return (
      (allocation.TemporaryPercentageAllocations || allocation.PermanentPercentageAllocations) / 100
    );
  }

  public SplitAllocations(payline: Payline, pay: EmployeePay): PayrollAllocationViewModel[] {
    var skipManualCheck = this._isManualCheckWithPermanentAllocations(payline);

    if (!skipManualCheck && payline.Allocations && Object.keys(payline.Allocations).length) {
      // We have allocations on this payline

      var allocationVMs = Object.entries(payline.Allocations).map(([id, allocation]) => {
        const jobCode = this.jobCodes.find(x => x.Id == +id);
        return new PayrollAllocationViewModel(payline, allocation, jobCode?.Value, this);
      });

      // Resolve any deltas of at least .01
      this._resolveTaxes(payline, allocationVMs);
      this._resolveEarnings(payline, allocationVMs);
      this._resolveDeductions(payline, allocationVMs);

      if (allocationVMs.length > 1) {
        // Add the view model for the total "payline"
        allocationVMs.push(
          new PayrollAllocationViewModel(
            payline,
            {
              ...new LaborAllocation(),
              ...{
                PermanentPercentageAllocations: 100,
                TemporaryPercentageAllocations: null,
                TemporaryHourlyAllocations: null,
                ResultPercentage: 100
              }
            },
            '__TOTAL__',
            this
          )
        );
      }

      return allocationVMs;
    } else {
      // We don't have allocations on this payline
      let noAllocText = 'None';
      if (this.allocTypeId == LaborAllocationTypes.Department) {
        noAllocText = pay.DepartmentId.toString();
      }

      return [
        new PayrollAllocationViewModel(
          payline,
          {
            ...new LaborAllocation(),
            ...{
              PermanentPercentageAllocations: 100,
              TemporaryPercentageAllocations: null,
              TemporaryHourlyAllocations: null,
              ResultPercentage: 100
            }
          },
          noAllocText,
          this
        )
      ];
    }
  }

  public _getDeductionConfig(deductionId): CompanyDeductionConfig {
    let config;

    this.configSetupData.Deductions.forEach(ded => {
      if (ded.Id == deductionId) {
        config = ded;
      }
    });

    return config;
  }

  public _getEarningConfig(earningId): CompanyEarningConfig {
    let config;

    this.configSetupData.Earnings.forEach(earn => {
      if (earn.Id == earningId) {
        config = earn;
      }
    });

    return config;
  }

  private _isManualCheckWithPermanentAllocations(payline: Payline) {
    return (
      payline.PaylineType == PaylineType.Manual &&
      payline.Allocations &&
      Object.values(payline.Allocations).every(
        a =>
          (a.TemporaryHourlyAllocations == null ||
            Object.keys(a.TemporaryHourlyAllocations).length == 0) &&
          a.TemporaryPercentageAllocations == null
      )
    );
  }

  //
  // Find any delta of at least .01 and apply it to the last allocation
  //
  private _resolveTaxes(payline: Payline, allocations: PayrollAllocationViewModel[]) {
    var sums = this._sumArray(
      payline.Taxes,
      t => t.TaxCode,
      t => t.CurrentAmount,
      allocations
    );

    Object.entries(sums).forEach(([id, value]) => {
      var tax = payline.Taxes.find(t => t.TaxCode == id);
      var delta = tax.CurrentAmount - value;
      //Anything .005 -> .01 will be rounded up to .01
      if (Math.abs(delta) >= 0.005) {
        allocations[allocations.length - 1].taxRounding[id] = delta;
      }
    });
  }

  private _resolveEarnings(payline: Payline, allocations: PayrollAllocationViewModel[]) {
    var sums = this._sumArray(
      payline.Earnings,
      e => e.EarningId,
      e => e.CurrentAmount,
      allocations
    );

    Object.entries(sums).forEach(([id, value]) => {
      var earning = payline.Earnings.find(earn => earn.EarningId == id);
      var delta = earning.CurrentAmount - value;
      //Anything .005 -> .01 will be rounded up to .01
      if (Math.abs(delta) >= 0.005) {
        allocations[allocations.length - 1].earnRounding[earning.EarningId] = delta;
      }
    });
  }

  private _resolveDeductions(payline: Payline, allocations: PayrollAllocationViewModel[]) {
    var sums = this._sumArray(
      payline.Deductions,
      d => d.DeductionCode,
      d => d.CurrentAmount,
      allocations
    );

    Object.entries(sums).forEach(([id, value]) => {
      var deduction = payline.Deductions.find(ded => ded.DeductionCode == id);
      var delta = deduction.CurrentAmount - value;
      //Anything .005 -> .01 will be rounded up to .01
      if (Math.abs(delta) >= 0.005) {
        allocations[allocations.length - 1].deductionRounding[deduction.DeductionCode] = delta;
      }
    });
  }

  //
  // Returns the sum of the dollar amount for all allocations based on the array and lambdas passed into it
  // array -> Intended to be Earnings, Taxes, or Deductions
  // getId -> Lambda for what ID to store the sum of each item from array
  // getValue -> Lambda for getting the value of each item from array
  // allocations -> All allocations for a payline
  //
  private _sumArray<Type>(
    array: Type[],
    getId: (Type) => string,
    getValue: (Type) => number,
    allcoations: PayrollAllocationViewModel[]
  ) {
    let sums: Record<string, number> = {};
    allcoations.forEach(allocation => {
      array.forEach(item => {
        var value = allocation.calculatePercentageValue(getValue(item));
        var id = getId(item);
        if (sums[id] == null) {
          sums[id] = 0;
        }

        sums[id] += value;
      });
    });

    return sums;
  }
}

export class PayrollAllocationViewModel {
  public taxRounding: Record<string, number> = {};
  public earnRounding: Record<string, number> = {};
  public deductionRounding: Record<string, number> = {};

  constructor(
    public payline: Payline,
    public allocation: LaborAllocation,
    public name: string,
    private laborSvc: LaborAllocationService
  ) {}

  get filteredEarnings() {
    return this.payline.Earnings.filter(e => e.CurrentAmount);
  }

  calculateAmount(earn: Earning) {
    if (this.laborSvc.IsPercentageAllocation(this.allocation)) {
      const value = this.laborSvc.GetPercentageAllocation(this.allocation) * earn.CurrentAmount;

      var delta = this.earnRounding[earn.EarningId] ?? 0;

      const index = this.payline.Earnings.indexOf(earn);
      if (index != this.payline.Earnings.length - 1) {
        delta = 0;
      }

      return value + delta;
    } else {
      return this.allocation.TemporaryHourlyAllocations[earn.EarningId]?.ResultAmount ?? 0;
    }
  }

  calculateHours(earn: Earning) {
    if (this.name == '__TOTAL__') {
      this.laborSvc.CalculateTotalHours(earn, this.payline);
    }

    return this.laborSvc.CalculateHours(earn, this.allocation);
  }

  calculateRate(earn: Earning) {
    if (this.name == '__TOTAL__') {
      return this.laborSvc.CalculateTotalRate(earn, this.payline);
    }

    return this.laborSvc.CalculateRate(earn, this.payline, this.allocation);
  }

  public calculatePercentageValue(amount: number) {
    var percent = this.allocation.ResultPercentage;
    return this.round(amount * (percent / 100));
  }

  public calculateTax(tax: Tax) {
    var total = this.calculatePercentageValue(tax.CurrentAmount);
    var delta = this.taxRounding[tax.TaxCode] ?? 0;
    return total + delta;
  }

  public calculateDeduction(deduction: Deduction) {
    var total = this.calculatePercentageValue(deduction.CurrentAmount);
    var delta = this.deductionRounding[deduction.DeductionCode] ?? 0;
    return total + delta;
  }

  private round(value: number) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
